// frontend/src/configuration/api.ts
import axios from "axios";
import { logout, refreshToken } from "../service/auth";
import { UserRole } from "../enums/userEnums";

declare module "axios" {
  interface AxiosRequestConfig {
    _skipAuthRefresh?: boolean;
    _retry?: boolean;
  }
}
const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_HOST,
  withCredentials: true,
});

interface TokenPayload {
  id: string;
  emailAddress: string;
  role: UserRole;
  iat: number;
  exp: number;
}

export const decodeToken = (token: string): TokenPayload | null => {
  try {
    const payloadBase64 = token.split(".")[1];
    const payload = atob(payloadBase64.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(payload) as TokenPayload;
  } catch (error) {
    console.error("Token decoding failed:", error);
    return null;
  }
};

axiosInstance.interceptors.request.use(async (config) => {
  // Skip modification if marked to skip auth
  if (config._skipAuthRefresh) {
    return config;
  }

  try {
    const authData = JSON.parse(localStorage.getItem("authData") || "{}");
    if (!authData.token) {
      return config;
    }

    const payload = decodeToken(authData.token);
    if (!payload) {
      return config;
    }

    // Set header in any case
    config.headers.Authorization = `Bearer ${authData.token}`;

    // Check if token is expired or about to expire (within 5 minutes)
    const isExpired = payload.exp * 1000 < Date.now();
    const isAboutToExpire = payload.exp * 1000 < Date.now() + 5 * 60 * 1000;

    if (isExpired || isAboutToExpire) {
      config._retry = true; // Mark for potential retry
    }

    return config;
  } catch (error) {
    console.error("Request interceptor error:", error);
    return config;
  }
});
// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (!error || !error.config) {
      return Promise.reject(error);
    }

    const originalRequest = error.config;

    if (originalRequest._skipAuthRefresh) {
      return Promise.reject(error);
    }

    // Only handle 401 errors and avoid infinite retry loops
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const { token } = await refreshToken();

        // Update both authData and the default header
        const authData = JSON.parse(localStorage.getItem("authData") || "{}");
        authData.token = token;
        localStorage.setItem("authData", JSON.stringify(authData));

        axiosInstance.defaults.headers.common[
          "Authorization"
        ] = `Bearer ${token}`;
        originalRequest.headers["Authorization"] = `Bearer ${token}`;

        return axiosInstance(originalRequest);
      } catch (refreshError) {
        console.error("Refresh token failed:", refreshError);
        await logout();
        window.location.href = "/login";
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default axiosInstance;
